// Fallback for when the HTTP fetch of the matching file under
// src/assets/data/print-labels-data/label/ fails. GENERATED FROM THAT FILE - keep
// the two identical: a drifted copy means a fetch failure quietly prints a
// different label, which is the worst moment to find the difference.
export const DEFAULT_DYMO_STYLES: string = `
/* =============================================================================
   Dymo label styles — 3in x 1in thermal label, one per physical container.

   LITERAL VALUES ONLY — NO var(), NO DESIGN TOKENS.

   This sheet is inlined verbatim into the print iframe, which loads nothing else:
   src/theme/variables.css is not there, so any custom property from the app's
   token layer resolves to nothing and the label prints unstyled on real hardware.
   That makes this a deliberate exception to the token convention now governing
   every other stylesheet in this repo. Do not centralize these values.
   ============================================================================= */

/* ---- CALIBRATION KNOBS ------------------------------------------------------
   Everything in this block is meant to be tuned against a real Dymo; everything
   below it is layout only and should not need touching.

   The size order is deliberate. Job number / PO and "BOX n of N" are what gets
   read across a warehouse aisle, so they are the largest text on the label and
   the supporting lines are small. Shrinking the QR is the first thing to try if
   a long job name is being truncated too aggressively.
   -------------------------------------------------------------------------- */
.qr-box         { width: 0.5in; height: 0.5in; }  /* printed QR square; ~0.5in is about the floor for scanning off shrink wrap */
.line-ids       { font-size: 12pt; }              /* job number + PO number */
.line-container { font-size: 11pt; }              /* BOX n of N + whole-delivery quantity */
.line-detail    { font-size: 8pt; }               /* job name / vendor + date / tech + shelf */
.dymo-label     { padding: 0.075in; }             /* label edge margin */
.label-text     { row-gap: 0.01in; }              /* space between text lines */
/* ---- end calibration knobs ---------------------------------------------- */

@page {
  size: 3in 1in; /* Match the label size */
  margin: 0; /* Remove default margins */
}

body {
  margin: 0;
  padding: 0;
}

/* Text column on the left, QR square pinned to the right. */
.dymo-label {
  width: 100%; /* Utilize full label width */
  height: 100%; /* Utilize full label height */
  display: flex;
  flex-direction: row;
  align-items: center;
  box-sizing: border-box;
  font-family: Arial, sans-serif;
  color: black;
  /*transform: rotate(90deg); Adjust for Dymo orientation */
  transform-origin: center;
  font-weight: bold;
}

/* Takes whatever width the QR does not, and never pushes it off the label. */
.label-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.line {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
  white-space: nowrap; /* Prevent wrapping */
  overflow: hidden; /* Hide overflowing text */
  text-overflow: ellipsis; /* Add ellipses for truncated text */
  line-height: 1.15;
}

/* The free-text fields truncate rather than shove the QR or their line-mate. */
.job-name,
.vendor,
.tech,
.container {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.po,
.date,
.qty {
  margin-left: auto;
  padding-left: 0.06in;
  flex: 0 0 auto;
}

/* The QR keeps its square: a long job name must never squeeze it out of scanning
   range, which is the whole reason it is on the label.

   TWIN of .sheet-qr in large-label-styles.css — same idea (a fixed square that
   flex-centres the spliced SVG and collapses when there is none), and the two stay
   duplicated rather than sharing a class. Not a judgement call: each sheet is
   injected ALONE into its own print iframe, so there is no cascade a shared sheet
   could reach. Sharing is impossible here by construction.

   Divergences from the twin: 0.5in vs 3in, and this one carries a left margin to
   hold it off the text column beside it, where the sheet's is centred on an
   otherwise empty page and needs none. */
.qr-box {
  flex: 0 0 auto;
  margin-left: 0.06in;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* A legacy log has no item rows and no QR — give the text the whole label back
   rather than printing around an empty square. */
.qr-box:empty {
  display: none;
}

/* The spliced <svg> carries a viewBox and no width/height (PrintService strips
   them), so it scales to whatever the calibration block sets above.

   Body is byte-identical to .sheet-qr svg in large-label-styles.css; see the twin
   note on .qr-box above for why these two sheets cannot share it. */
.qr-box svg {
  width: 100%;
  height: 100%;
  display: block;
}
`;
